/**
 * AI Guide notifications are user-facing UI messages generated in response
 * to some programmatic prompt, EG a webapp event.
 * This file exists to keep track of and provide access to relevant values.
 *
 */
type NotificationVariable = "notificationID" | "notificationConversation";

const notificationVariables: Map<NotificationVariable, unknown> = new Map();

/**
 * Set an AI Guide notification variable.
 *
 * @param key A string representing the name of the variable.
 * @param value A value to set the variable to.
 * If this is nullish, the variable will be
 * deleted.
 * @returns void
 */
export const setNotificationVariable = (
    key: NotificationVariable,
    value: unknown,
): void => {
    if (value == null) {
        clearNotificationVariable(key);
    } else {
        notificationVariables.set(key, value);
    }
};

/**
 * Get a notification variable value.
 *
 * @param key A string representing the name of the variable.
 * @returns The current value of the notification variable or undefined if not set.
 */
export const getNotificationVariable = (
    key: NotificationVariable,
): any | undefined => notificationVariables.get(key);

/**
 * Clear a notification variable.
 *
 * @param key A string representing the name of the variable.
 * @returns void
 */
export const clearNotificationVariable = (key: NotificationVariable): void => {
    notificationVariables.delete(key);
};

/**
 * Clear all notification variables.
 *
 * @param keys An array of strings representing the names of the variables.
 * If this is nullish, all variables will be deleted.
 *
 * @returns void
 */
export const clearNotificationVariables = (
    keys?: Array<NotificationVariable>,
): void => {
    if (keys) {
        keys.forEach(clearNotificationVariable);
    } else {
        notificationVariables.clear();
    }
};
